/**
 * What a step can return, and where each answer goes — as one list.
 *
 * THE PROBLEM THIS SOLVES. On the wire a node declares any number of NAMED
 * typed output fields and its edges are a little expression language
 * (`{field, op, value}` in order, with a `default` floor). That generality is
 * real — the cross-encoder returns two numbers, and calibration writes
 * conditions programmatically — but it is needed by almost nothing a person
 * builds, and the editor used to expose it one-to-one. To say "true goes
 * here, false goes there" you had to invent a variable name, give it a type,
 * then pick that name back out of a dropdown, pick an operator, type a value,
 * and keep an `otherwise` in sync. Six controls for a thing with exactly two
 * outcomes and no degrees of freedom.
 *
 * So: a step that declares ONE output has OUTCOMES, and an outcome is just a
 * label and where it goes. The field name is never asked for and never shown.
 *
 *   true / false   two outcomes: True, False
 *   one of …       one outcome per member
 *   number         ranges the person writes — "≥ 0.692 and < 0.71" — each
 *                  one an outcome
 *
 * Ranges are free-form, so a person can say exactly where a boundary sits and
 * which side of it a value falls on. The price is that ranges can overlap or
 * leave a gap, and `analyse()` finds both: an overlap is refused (the editor
 * never writes one, and the server rejects one), a gap is allowed but named,
 * and whatever falls in it goes to an "Anything else" outcome — the default
 * edge every step must end with.
 *
 * NOTHING HERE CHANGES THE WIRE FORMAT. These functions read the stored edges
 * and write them back in the same language the executor, the validator and
 * calibration already speak. `read()` returns null for a graph it cannot
 * express as a plain outcome list — a hand-written condition, a step with two
 * outputs — and the editor then shows the expression editor rather than
 * rewriting something it does not understand.
 */

/** The one output a step declares, or null when it declares none or several. */
export function singleOutput(node) {
  const entries = Object.entries(node?.output || {});
  return entries.length === 1 ? entries[0] : null;
}

/** Does this stored condition match `v`? Mirrors the executor's edges.matches. */
function condMatches(when, v) {
  const r = when?.range || {};
  switch (when?.op) {
    case 'eq': return v === when.value;
    case 'neq': return v !== when.value;
    case 'in': return Array.isArray(when.value) && when.value.includes(v);
    case 'not_in': return Array.isArray(when.value) && !when.value.includes(v);
    case 'lt': return v < when.value;
    case 'lte': return v <= when.value;
    case 'gt': return v > when.value;
    case 'gte': return v >= when.value;
    case 'between':
      return (r.inclusive_min === false ? v > r.min : v >= r.min)
        && (r.inclusive_max === false ? v < r.max : v <= r.max);
    default: return false;
  }
}

/** Where the walk goes when the step returns `v`: first match, else the default. */
function targetFor(edges, field, v) {
  let fallback = null;
  for (const e of edges || []) {
    if (e.default !== undefined) { fallback = e.default; continue; }
    if (e.when && e.when.field === field && condMatches(e.when, v)) return e.to;
  }
  return fallback;
}

const num = (n, fallback = 0) => (Number.isFinite(n) ? n : fallback);
/** Trim float noise from a boundary so "0.8" never renders as 0.7999999999999999. */
const tidy = (n) => Number(Number(n).toFixed(6));

/** Every boundary a condition mentions — the candidate cut points. */
function boundariesOf(edges) {
  const out = [];
  for (const e of edges) {
    if (e.default !== undefined) continue;
    const w = e.when;
    const r = (w && w.range) || {};
    if (w.op === 'between') out.push(r.min, r.max);
    else out.push(w.value);
  }
  return out.filter(Number.isFinite);
}

/**
 * A number step's edges as buckets, or null when they do not cut the range
 * cleanly into left-closed pieces.
 *
 * Rather than insisting on the canonical form this module writes, take every
 * boundary the conditions mention, cut there, and ask the routing itself
 * where each piece goes — so a judge stored the way anyone would write it by
 * hand ("score >= 0.8 → pass, otherwise fail") reads back as two buckets. A
 * piece whose ends disagree with its middle is not a bucket — `lte 0.5` puts
 * the boundary on the wrong side, `eq 0.5` is a point — and those keep the
 * expression editor rather than being silently rounded into something else.
 */
function readBuckets(edges, field, spec) {
  const lo = num(spec.min); const hi = num(spec.max, 1);
  if (!(hi > lo)) return null;
  // Where the range is cut is declared on the output, because a bucket that
  // leads nowhere yet has no edge to hide in — and an unconnected split has
  // to survive the render that follows adding it. Boundaries the edges
  // mention are folded in too, so a graph written by hand or by calibration
  // still reads, and the two can never silently disagree: every bucket is
  // probed against the real routing below.
  const declared = Array.isArray(spec.cuts) ? spec.cuts : [];
  const cuts = [...new Set([...declared, ...boundariesOf(edges)].map(tidy))].filter((c) => c > lo && c < hi).sort((a, b) => a - b);
  const bounds = [lo, ...cuts, hi];
  const to = [];
  for (let i = 0; i < bounds.length - 1; i += 1) {
    const a = bounds[i]; const b = bounds[i + 1];
    const last = i === bounds.length - 2;
    const probes = [a, (a + b) / 2, ...(last ? [b] : [])];
    const seen = probes.map((v) => targetFor(edges, field, v));
    if (seen.some((t) => t !== seen[0])) return null;   // null is a bucket that leads nowhere yet
    to.push(seen[0]);
  }
  return { cuts, to };
}

/**
 * The step's outcomes, in the order they are shown.
 * @returns {{type, outcomes: [{key, label, to}], values?, cuts?, spec}|null}
 */
export function read(node) {
  const one = singleOutput(node);
  if (!one) return null;
  const [field, spec] = one;
  const edges = node.edges || [];
  // 0 defaults is a draft someone is still wiring; 2 is malformed.
  if (edges.filter((e) => e.default !== undefined).length > 1) return null;
  // A condition on anything but this step's own output is hand-written.
  if (edges.some((e) => e.default === undefined && (!e.when || e.when.field !== field))) return null;

  if (spec.type === 'boolean') {
    return {
      type: 'boolean',
      spec,
      outcomes: [
        { key: 'true', label: 'True', to: targetFor(edges, field, true) },
        { key: 'false', label: 'False', to: targetFor(edges, field, false) },
      ],
    };
  }

  if (spec.type === 'enum') {
    const values = spec.values || [];
    if (!values.length) return null;
    return {
      type: 'enum',
      spec,
      values,
      outcomes: values.map((v) => ({ key: v, label: v, to: targetFor(edges, field, v) })),
    };
  }

  if (spec.type === 'number') return readNumber(node, field, spec, edges);

  return null;
}

/**
 * Edges for a step whose outcomes go to `targets` (aligned to `read()`'s
 * order). Every form ends in the default, which is the LAST outcome: the
 * executor type-checks the value against the declared output before routing,
 * so nothing but that outcome can reach it.
 */
function build(type, field, spec, targets) {
  const wired = targets.every(Boolean);
  if (wired && new Set(targets).size === 1) return [{ default: targets[0] }];
  const conditionFor = (i) => (type === 'boolean'
    ? { field, op: 'eq', value: i === 0 }
    : { field, op: 'eq', value: (spec.values || [])[i] });
  const rows = targets.map((to, i) => ({ when: conditionFor(i), to })).filter((r) => r.to);
  // Fully wired: the last outcome is the default, which is what makes the set
  // exhaustive. Half wired: explicit conditions and NO default — the graph is
  // invalid until the rest is connected, and saying so is the point.
  if (!wired || !rows.length) return rows;
  return [...rows.slice(0, -1), { default: rows[rows.length - 1].to }];
}

/** Rewrite `node`'s edges so the outcome at `index` goes to `to` — or nowhere, when `to` is null. */
export function route(node, index, to) {
  const shape = read(node);
  if (!shape) return node.edges;
  const [field] = singleOutput(node);
  const targets = shape.outcomes.map((o, i) => (i === index ? (to || null) : o.to));
  if (shape.type === 'number') {
    const n = shape.ranges.length;
    return buildNumber(field, shape.spec, shape.ranges, targets.slice(0, n), shape.gaps.length ? targets[n] : null);
  }
  return build(shape.type, field, shape.spec, targets);
}

/** Targets kept by position when the outcome list grows or shrinks. */
function keep(shape, count) {
  const was = shape ? shape.outcomes.filter((o) => o.key !== ELSE).map((o) => o.to) : [];
  return Array.from({ length: count }, (_, i) => was[i] ?? null);
}

const SPEC_FOR = {
  boolean: () => ({ type: 'boolean' }),
  number: () => ({ type: 'number', min: 0, max: 1, ranges: [{ min: 0, max: 0.5, max_inclusive: false }, { min: 0.5, max: 1 }] }),
  enum: () => ({ type: 'enum', values: ['yes', 'no'] }),
};

/**
 * Change what the step returns. Every outcome lands where the step already
 * went, so the graph stays valid through the change and the person re-points
 * only what they mean to.
 */
export function setType(node, type) {
  const [field] = singleOutput(node) || ['value'];
  return { output: { [field]: SPEC_FOR[type]() }, edges: [] };
}

// ---------------------------------------------------------------------------
// Numbers: ranges
// ---------------------------------------------------------------------------

/** The key of the outcome that catches what no range covers. */
export const ELSE = 'else';

/**
 * A range is {min, max, min_inclusive?, max_inclusive?}; both ends are
 * included unless marked false, as the executor's `between` reads them.
 */
const inc = (r, end) => r[`${end}_inclusive`] !== false;
const norm = (r) => ({
  min: tidy(r.min), max: tidy(r.max),
  ...(r.min_inclusive === false ? { min_inclusive: false } : {}),
  ...(r.max_inclusive === false ? { max_inclusive: false } : {}),
});
const sortRanges = (rs) => [...rs].sort((a, b) => a.min - b.min || (inc(b, 'min') - inc(a, 'min')) || a.max - b.max);

/** Does range `r` contain `v`? */
export function contains(r, v) {
  return (inc(r, 'min') ? v >= r.min : v > r.min) && (inc(r, 'max') ? v <= r.max : v < r.max);
}

function overlaps(a, b) {
  const lo = Math.max(a.min, b.min);
  const hi = Math.min(a.max, b.max);
  if (lo < hi) return true;
  if (lo > hi) return false;
  return contains(a, lo) && contains(b, lo);   // touching at one point: only if both include it
}

/** How a range reads: "< 0.5", "0.5 – 0.8", "≥ 0.8", or its exact ends when they are not the usual ones. */
export function rangeLabel(r, lo, hi) {
  if (r.min === r.max) return `= ${r.min}`;
  const fromBottom = r.min <= lo && inc(r, 'min');
  const toTop = r.max >= hi && inc(r, 'max');
  if (fromBottom && toTop) return 'any value';
  if (fromBottom) return `${inc(r, 'max') ? '≤' : '<'} ${r.max}`;
  if (toTop) return `${inc(r, 'min') ? '≥' : '>'} ${r.min}`;
  if (inc(r, 'min') && !inc(r, 'max')) return `${r.min} – ${r.max}`;
  return `${inc(r, 'min') ? '≥' : '>'} ${r.min}, ${inc(r, 'max') ? '≤' : '<'} ${r.max}`;
}

/** How a gap reads, in words. */
export function gapLabel(g) {
  if (g.min === g.max) return `exactly ${g.min}`;
  return `${inc(g, 'min') ? '' : 'just above '}${g.min} to ${inc(g, 'max') ? '' : 'just under '}${g.max}`;
}

/**
 * Everything wrong with a list of ranges over the step's scale [lo, hi]:
 *   problems[i]  what is wrong with range i on its own, or with its neighbours
 *   overlapping  pairs [i, j] that share values — never written
 *   gaps         stretches of [lo, hi] no range covers — allowed, and named
 *   ok           nothing stops these being written
 */
export function analyse(ranges, lo, hi) {
  const problems = ranges.map(() => null);
  const say = (i, msg) => { if (!problems[i]) problems[i] = msg; };
  ranges.forEach((r, i) => {
    if (!Number.isFinite(r.min) || !Number.isFinite(r.max)) say(i, 'Both ends need a number.');
    else if (r.min > r.max) say(i, 'The start is above the end.');
    else if (r.min === r.max && !(inc(r, 'min') && inc(r, 'max'))) say(i, 'This range is empty: it starts and ends at the same number but leaves that number out.');
    else if (r.min < lo || r.max > hi) say(i, `Outside the scale (${lo} to ${hi}).`);
  });
  const overlapping = [];
  for (let i = 0; i < ranges.length; i += 1) {
    for (let j = i + 1; j < ranges.length; j += 1) {
      if (problems[i] || problems[j]) continue;
      if (overlaps(ranges[i], ranges[j])) {
        overlapping.push([i, j]);
        say(i, `Overlaps range ${j + 1}; a value can only go one way.`);
        say(j, `Overlaps range ${i + 1}; a value can only go one way.`);
      }
    }
  }
  // Walk the scale bottom to top; whatever no range reaches is a gap.
  const gaps = [];
  const valid = sortRanges(ranges.filter((r, i) => !problems[i]));
  let at = { v: lo, open: true };   // the lowest value not yet covered; `open` = that value itself is uncovered
  for (const r of valid) {
    const startsAfter = r.min > at.v || (r.min === at.v && at.open && !inc(r, 'min'));
    if (startsAfter) gaps.push(norm({ min: at.v, max: r.min, min_inclusive: at.open, max_inclusive: !inc(r, 'min') }));
    if (r.max > at.v) at = { v: r.max, open: !inc(r, 'max') };
    else if (r.max === at.v && inc(r, 'max')) at = { v: r.max, open: false };
  }
  if (at.v < hi || (at.v === hi && at.open)) gaps.push(norm({ min: at.v, max: hi, min_inclusive: at.open, max_inclusive: true }));
  return { problems, overlapping, gaps: gaps.filter((g) => g.min < g.max || (inc(g, 'min') && inc(g, 'max'))), ok: problems.every((p) => !p) };
}

/** A range as the executor's `between` condition. */
function between(field, r) {
  return {
    field, op: 'between',
    range: { min: r.min, max: r.max, ...(inc(r, 'min') ? {} : { inclusive_min: false }), ...(inc(r, 'max') ? {} : { inclusive_max: false }) },
  };
}

/** Condition → range, clipped to the scale; null for what is not one range (neq). */
function rangeOfCondition(w, lo, hi) {
  const v = w.value;
  switch (w.op) {
    case 'between': return norm({ min: w.range.min, max: w.range.max, min_inclusive: w.range.inclusive_min !== false, max_inclusive: w.range.inclusive_max !== false });
    case 'eq': return norm({ min: v, max: v });
    case 'lt': return norm({ min: lo, max: v, max_inclusive: false });
    case 'lte': return norm({ min: lo, max: v });
    case 'gt': return norm({ min: v, max: hi, min_inclusive: false });
    case 'gte': return norm({ min: v, max: hi });
    default: return null;
  }
}

/** A point inside `r` to ask the routing about. */
const probe = (r) => (r.min === r.max ? r.min : (r.min + r.max) / 2);

/** The first explicit condition that catches `v`, ignoring the default. */
function explicitTarget(edges, field, v) {
  for (const e of edges || []) {
    if (e.default !== undefined) continue;
    if (e.when && e.when.field === field && condMatches(e.when, v)) return e.to;
  }
  return null;
}

/**
 * A number step's ranges and where each goes.
 *
 * Where the ranges are is declared on the output (`spec.ranges`), because a
 * range that leads nowhere yet has no edge to hide in. A graph written before
 * that (split points, or conditions by hand or by calibration) is read from
 * its edges. Either way every range is probed against the real routing, so
 * the list and the edges can never silently disagree; a range whose inside
 * goes two ways is not a range, and the expression editor takes over.
 */
function readNumber(node, field, spec, edges) {
  const lo = num(spec.min); const hi = num(spec.max, 1);
  if (!(hi > lo)) return null;
  let ranges;
  if (Array.isArray(spec.ranges)) ranges = spec.ranges.map(norm);
  else {
    const buckets = readBuckets(edges, field, spec);
    if (buckets) {
      const bounds = [lo, ...buckets.cuts, hi];
      ranges = bounds.slice(0, -1).map((a, i) => norm({ min: a, max: bounds[i + 1], ...(i === bounds.length - 2 ? {} : { max_inclusive: false }) }));
    } else {
      ranges = [];
      for (const e of edges) {
        if (e.default !== undefined) continue;
        const r = rangeOfCondition(e.when, lo, hi);
        if (!r) return null;
        ranges.push(r);
      }
      if (analyse(ranges, lo, hi).overlapping.length) return null;   // first-match order would matter; not a list
      // What the conditions leave uncovered is where the default already
      // sends it, so it reads as ranges of its own, not as a gap to warn about.
      if (edges.some((e) => e.default !== undefined)) ranges.push(...analyse(ranges, lo, hi).gaps);
      ranges = sortRanges(ranges);
    }
  }
  const { gaps } = analyse(ranges, lo, hi);
  const fallback = (edges.find((e) => e.default !== undefined) || {}).default ?? null;
  const to = [];
  for (let i = 0; i < ranges.length; i += 1) {
    const r = ranges[i];
    const inside = [probe(r), ...(inc(r, 'min') ? [r.min] : []), ...(inc(r, 'max') ? [r.max] : [])].filter((v) => v >= lo && v <= hi);
    const seen = inside.map((v) => explicitTarget(edges, field, v));
    if (seen.some((t) => t !== seen[0])) return null;
    // With no gap, a range with no condition of its own is where the default
    // lands (`buildNumber` writes the last one that way; a hand-written
    // "≥ 0.8 → pass, otherwise fail" leaves the lower one to it). With a gap,
    // the default is "anything else", and a range without a condition is
    // simply not connected yet.
    to.push(seen[0] ?? (gaps.length ? null : fallback));
  }
  const outcomes = ranges.map((r, i) => ({ key: String(i), label: rangeLabel(r, lo, hi), to: to[i] }));
  if (gaps.length) outcomes.push({ key: ELSE, label: 'Anything else', to: fallback });
  return { type: 'number', spec, ranges, gaps, outcomes };
}

/**
 * Edges for a number step. Each connected range is a `between`. With no gap
 * the last range is the default, which makes the set exhaustive; with a gap
 * the default is "anything else". Until every range and the gap are wired
 * there is no default, so the graph is invalid and the editor says so.
 */
function buildNumber(field, spec, ranges, targets, elseTo) {
  const lo = num(spec.min); const hi = num(spec.max, 1);
  const { gaps } = analyse(ranges, lo, hi);
  const rows = ranges.map((r, i) => ({ when: between(field, r), to: targets[i] })).filter((row) => row.to);
  const wired = targets.every(Boolean) && (!gaps.length || elseTo);
  if (!gaps.length) return wired && rows.length ? [...rows.slice(0, -1), { default: rows[rows.length - 1].to }] : rows;
  return elseTo ? [...rows, { default: elseTo }] : rows;
}

/**
 * Number: the ranges. Refused (null) when any is malformed or two overlap;
 * a gap is fine. Targets follow each range by position unless `targets`
 * says otherwise (a removed range takes its target with it); anything else
 * keeps its target.
 */
export function setRanges(node, rawRanges, targets = null) {
  const shape = read(node);
  const [field, spec] = singleOutput(node);
  const ranges = rawRanges.map(norm);
  if (!analyse(ranges, num(spec.min), num(spec.max, 1)).ok) return null;
  const next = { ...spec, ranges };
  delete next.cuts;
  const elseTo = shape?.outcomes.find((o) => o.key === ELSE)?.to ?? null;
  const to = targets ? ranges.map((_, i) => targets[i] ?? null) : keep(shape, ranges.length);
  return { output: { [field]: next }, edges: buildNumber(field, next, ranges, to, elseTo) };
}

/**
 * Number: a new range where it fits — the first gap, or else the top half of
 * the last range, so adding one never makes an overlap.
 */
export function withNewRange(ranges, lo, hi) {
  const { gaps } = analyse(ranges, lo, hi);
  if (gaps.length) return [...ranges, gaps[0]];
  if (!ranges.length) return [norm({ min: lo, max: hi })];
  const sorted = sortRanges(ranges);
  const top = sorted[sorted.length - 1];
  if (top.max - top.min <= 0) return ranges;
  const mid = tidy((top.min + top.max) / 2);
  return [...sorted.slice(0, -1), norm({ ...top, max: mid, max_inclusive: false }), norm({ min: mid, max: top.max, max_inclusive: inc(top, 'max') })];
}

/** Number: the scale the step returns on. Ranges are kept; any now outside it are named, not dropped. */
export function setRange(node, min, max) {
  const [field, spec] = singleOutput(node);
  const shape = read(node);
  const next = { ...spec, min, max, ranges: shape ? shape.ranges : [] };
  delete next.cuts;
  const elseTo = shape?.outcomes.find((o) => o.key === ELSE)?.to ?? null;
  if (!analyse(next.ranges, min, max).ok) return { output: { [field]: next }, edges: node.edges };
  return { output: { [field]: next }, edges: buildNumber(field, next, next.ranges, keep(shape, next.ranges.length), elseTo) };
}

/** One of: the members. Each is an outcome, so routing follows them by position. */
export function setMembers(node, rawMembers) {
  const [field, spec] = singleOutput(node);
  const members = [...new Set(rawMembers.filter(Boolean))];
  if (!members.length) return null;
  const next = { ...spec, type: 'enum', values: members };
  const shape = read(node);
  const targets = keep(shape, members.length);
  return { output: { [field]: next }, edges: build('enum', field, next, targets) };
}
